"use client"

import { useAutoleadsLocale } from "../../../lib/locale-context"
import { useTr } from "../../../lib/use-tr"
import { TASK_BOARD_T } from "../../campaigns/[id]/sync/board/translations"
import { CRM_T } from "../crm-translations"
import { formatDistanceToNow } from "date-fns"
import { enUS, fr } from "date-fns/locale"
import type { CSSProperties } from "react"

import {
  CRM_ICONS,
  TINT_CLASS,
  crmColorTokens,
  initials,
  memberTokens,
} from "@/lib/apps/autoleads/crm"
import { STEP_META } from "@/lib/apps/autoleads/pipeline-steps"
import { cn } from "@/lib/utils"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import type {
  CrmColor,
  CrmIcon,
  CrmMember,
} from "@/nango/types/apps.autoleads/autoleads/Crm"
import type { PipelineStepType } from "@/nango/types/apps.autoleads/autoleads/MailingPipeline"

// A team-defined type as a small tinted square: the same chip in the list, the
// log and the settings, so a "Quote" always looks like a quote.
export function TypeIcon({
  icon,
  color,
  className,
}: {
  icon: CrmIcon
  color: CrmColor
  className?: string
}) {
  const Icon = CRM_ICONS[icon] ?? CRM_ICONS.note
  const tokens = crmColorTokens(color)
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-6 shrink-0 items-center justify-center rounded-md",
        TINT_CLASS,
        className,
      )}
      style={
        { "--crm-tint": tokens.tint, "--crm-ink": tokens.ink } as CSSProperties
      }
    >
      <Icon className="size-3.5" />
    </span>
  )
}

// A sequence step drawn with the board's own icon and tone, so a call step
// looks the same here as on the campaign board.
export function StepIcon({
  stepType,
  className,
}: {
  stepType: PipelineStepType
  className?: string
}) {
  const meta = STEP_META[stepType]
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-6 shrink-0 items-center justify-center rounded-md",
        meta.tone,
        className,
      )}
    >
      <meta.Icon className="size-3.5" />
    </span>
  )
}

export function useStepLabel() {
  const trBoard = useTr(TASK_BOARD_T)
  return (stepType: PipelineStepType | "") =>
    stepType ? trBoard(STEP_META[stepType].labelKey) : ""
}

export function MemberAvatar({
  member,
  size = "sm",
  showName = false,
}: {
  member: CrmMember
  size?: "sm" | "md"
  showName?: boolean
}) {
  const tokens = memberTokens(member.id)
  // The picture when the member has one (plain initials while it loads), their
  // tinted initials otherwise.
  const avatar = member.avatar_url ? (
    <Avatar className={cn("shrink-0", size === "sm" ? "size-6" : "size-8")}>
      <AvatarImage src={member.avatar_url} alt={member.name} />
      <AvatarFallback className={size === "sm" ? "text-3xs" : "text-xs"}>
        {initials(member.name)}
      </AvatarFallback>
    </Avatar>
  ) : (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-medium",
        TINT_CLASS,
        size === "sm" ? "text-3xs size-6" : "size-8 text-xs",
      )}
      style={
        { "--crm-tint": tokens.tint, "--crm-ink": tokens.ink } as CSSProperties
      }
    >
      {initials(member.name)}
    </span>
  )
  if (showName) {
    return (
      <span className="inline-flex min-w-0 items-center gap-2">
        {avatar}
        <span className="truncate text-sm">{member.name}</span>
      </span>
    )
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatar}</TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {member.name}
      </TooltipContent>
    </Tooltip>
  )
}

export function RelativeTime({ at }: { at: string }) {
  const locale = useAutoleadsLocale()
  return (
    <time dateTime={at}>
      {formatDistanceToNow(new Date(at), {
        addSuffix: true,
        locale: locale === "FR" ? fr : enUS,
      })}
    </time>
  )
}

// Previous / next under a paged CRM list. Hidden when everything fits.
export function CrmPager({
  page,
  totalPages,
  onPageChange,
}: {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}) {
  const tr = useTr(CRM_T)
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-between gap-3">
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        {tr("previous")}
      </Button>
      <span className="text-muted-foreground text-sm tabular-nums">
        {tr("pageOf")
          .replace("{page}", String(page))
          .replace("{total}", String(totalPages))}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        {tr("next")}
      </Button>
    </div>
  )
}

// A create or edit form laid in the page where the item will appear, never in
// a dialog stacked over the sheet: the rest of the page stays in view.
export function InlineForm({
  title,
  description,
  children,
  submitLabel,
  onSubmit,
  onCancel,
  pending = false,
  submitDisabled = false,
}: {
  title: string
  description?: string
  children: React.ReactNode
  submitLabel?: string
  onSubmit: () => void
  onCancel: () => void
  pending?: boolean
  submitDisabled?: boolean
}) {
  const tr = useTr(CRM_T)
  return (
    <form
      className="border-border bg-card space-y-4 rounded-lg border p-4"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <div className="space-y-0.5">
        <h3 className="text-sm font-medium">{title}</h3>
        {description ? (
          <p className="text-muted-foreground text-xs">{description}</p>
        ) : null}
      </div>
      {children}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          {tr("cancel")}
        </Button>
        <Button type="submit" size="sm" disabled={pending || submitDisabled}>
          {submitLabel ?? tr("save")}
        </Button>
      </div>
    </form>
  )
}
